import { CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Box, Select } from '@zillow/constellation-10';
import { format } from 'date-fns';
import { GraphWrapper } from '../../SharedComponents/GraphWrapper';
import { CustomGraphTooltip } from '../../SharedComponents/CustomGraphTooltip';
import { CustomGraphLegend } from '../../SharedComponents/CustomGraphLegend';
import { formatCurrency } from '../../../utils/formatCurrency';
import { formatStackedNumbers } from '../../../utils';
import { useMainLayout } from '../../layouts/MainPageLayout/MainLayoutContext';
import type {
  ListingCompDto,
  MonthlyPriceHistoryDto,
  PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { useMemo } from 'react';
import {
  bedroomSelectOptions,
  CompsMonthTick,
  getCompsColorMapping,
} from '../MarketData/Report/Graphs/RentComps/RentComps.utils';
import type { RentCompsChartDataPoint } from '../MarketData/Report/Graphs/RentComps/RentComps.types';

type AverageRentCompsGraphProps = {
  selectedProperty: PortfolioAnalyticsPropertyResponseDto;
  comps: Array<ListingCompDto>;
  numBedrooms: number;
  setNumBedrooms: (value: number) => void;
  priceHistoryMap: Record<string, Array<MonthlyPriceHistoryDto>> | undefined;
  isLoadingPriceHistory: boolean;
};

export const AverageRentCompsGraph = ({
  selectedProperty,
  comps,
  numBedrooms,
  setNumBedrooms,
  priceHistoryMap,
  isLoadingPriceHistory,
}: AverageRentCompsGraphProps) => {
  const { isFetchingInitialData } = useMainLayout();

  const { allCompNames, allCompsColorMapping, chartData } = useMemo(() => {
    // Find the selected property in the comps array
    const selectedListingAlias = selectedProperty.propertyDetails.listingAlias;
    const selectedPropertyComp = comps.find((comp) => comp.listingAlias === selectedListingAlias);

    // Get all comp names, ensuring selected property is always included first
    const competitorNames = comps
      .filter((comp) => comp.listingAlias !== selectedListingAlias)
      .map((comp) => comp.propertyName);
    const names = selectedPropertyComp ? [selectedPropertyComp.propertyName, ...competitorNames] : competitorNames;
    const colorMapping = getCompsColorMapping(names, selectedProperty.propertyDetails.listingName);

    const chartDataMap = new Map<string, RentCompsChartDataPoint>();

    // Collect all unique months across all comps
    const allMonthsSet = new Set<string>();
    comps.forEach((comp) => {
      priceHistoryMap?.[comp.listingAlias]?.forEach((monthData) => {
        const monthName = format(new Date(monthData.reportMonth.year, monthData.reportMonth.month - 1), 'MMM yyyy');
        allMonthsSet.add(monthName);
      });
    });

    // Initialize chart data for each month
    allMonthsSet.forEach((monthName) => {
      const [month, year] = monthName.split(' ');
      const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
      chartDataMap.set(monthName, {
        name: monthName,
        timestamp: new Date(parseInt(year), monthIndex).getTime(),
      });
    });

    // Process each comp's price history (including selected property)
    comps.forEach((comp) => {
      if (!names.includes(comp.propertyName)) return;

      priceHistoryMap?.[comp.listingAlias]?.forEach((monthData) => {
        const monthName = format(new Date(monthData.reportMonth.year, monthData.reportMonth.month - 1), 'MMM yyyy');
        const dataPoint = chartDataMap.get(monthName);
        if (!dataPoint) return;

        const price = monthData.priceByBedroom?.[numBedrooms]?.averagePrice;
        if (price) {
          dataPoint[comp.propertyName] = Math.round(price);
        }
      });
    });

    const chartDataArray = Array.from(chartDataMap.values()).sort((a, b) => a.timestamp - b.timestamp);

    return {
      allCompNames: names,
      allCompsColorMapping: colorMapping,
      chartData: chartDataArray,
    };
  }, [comps, selectedProperty, numBedrooms, priceHistoryMap]);
  const bedsLabel = bedroomSelectOptions.find((o) => o.value === numBedrooms)?.label ?? 'All Beds';

  const title = `Average rent comps - ${selectedProperty.propertyDetails.listingName}`;

  const allSeriesNames = allCompNames;

  const labelTypes: Record<string, string> = {};
  allSeriesNames.forEach((name) => {
    labelTypes[name] = 'line';
  });

  return (
    <GraphWrapper
      graphId="avg-rent-comps"
      title={title}
      subtitle={`How the average rent of competitors compares to the market average for ${bedsLabel}.`}
      loading={isFetchingInitialData || isLoadingPriceHistory}
      infoCards={[]}
      customFilters={
        <Box css={{ display: 'flex', gap: '200' }}>
          <Select
            size="md"
            value={numBedrooms}
            onChange={(e) => setNumBedrooms(Number(e.target.value))}
            css={{ maxWidth: '150px' }}
          >
            {bedroomSelectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Box>
      }
    >
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <YAxis tickFormatter={(value) => `$${formatStackedNumbers(value)}`} />
          <XAxis dataKey="name" interval={0} tick={<CompsMonthTick />} />
          <Tooltip formatter={(value) => formatCurrency(Number(value))} content={<CustomGraphTooltip />} />
          <Legend
            wrapperStyle={{ marginBottom: '-32px' }}
            content={
              <CustomGraphLegend
                labelTypes={labelTypes}
                filterPayload={(payload: Array<{ value?: string }>) => {
                  const filtered = payload.filter((entry) => !entry.value?.endsWith(' month to date'));
                  const order = [...allSeriesNames];
                  return filtered.sort((a, b) => order.indexOf(a.value ?? '') - order.indexOf(b.value ?? ''));
                }}
              />
            }
          />
          {allSeriesNames.map((name) => (
            <Line
              key={name}
              type="linear"
              dataKey={name}
              stroke={allCompsColorMapping[name] || '#000'}
              strokeWidth={3}
            />
          ))}
          {allSeriesNames.map((name) => (
            <Line
              key={`${name}-mtd`}
              type="linear"
              dataKey={`${name} month to date`}
              stroke={allCompsColorMapping[name] || '#000'}
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={false}
              legendType="none"
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </GraphWrapper>
  );
};
